import React, { useEffect, useState } from 'react';
import { Plus, X, ClipboardPaste, Loader2, Save } from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import { strategyLabApi } from '../../lib/api';
import type { Watchlist } from '../../types/strategyLab';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';

/** Parse pasted / typed free-form text into a deduped, upper-cased ticker list. */
export function parseSymbols(text: string): string[] {
  return Array.from(
    new Set(
      text
        .split(/[\s,;]+/)
        .map((t) => t.trim().toUpperCase())
        .filter((t) => /^[A-Z][A-Z0-9.\-]{0,9}$/.test(t)),
    ),
  );
}

interface WatchlistBuilderProps {
  editing: Watchlist | null;
  onDone: () => void;
}

const WatchlistBuilder: React.FC<WatchlistBuilderProps> = ({ editing, onDone }) => {
  const createWatchlist = useStrategyLabStore((s) => s.createWatchlist);
  const updateWatchlist = useStrategyLabStore((s) => s.updateWatchlist);
  const importWatchlist = useStrategyLabStore((s) => s.importWatchlist);
  const actionError = useStrategyLabStore((s) => s.actionError);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [symbols, setSymbols] = useState<string[]>([]);
  const [symbolInput, setSymbolInput] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Load full symbol list when switching into edit mode (list endpoint has only counts).
  useEffect(() => {
    let cancelled = false;
    if (editing) {
      setName(editing.name);
      setDescription(editing.description ?? '');
      setSymbols(editing.symbols ?? []);
      if (!editing.symbols) {
        setLoadingDetail(true);
        strategyLabApi
          .getWatchlist(editing.id)
          .then((detail) => {
            if (!cancelled) setSymbols(detail.symbols || []);
          })
          .catch(() => {
            /* surfaced via store on save; keep empty */
          })
          .finally(() => {
            if (!cancelled) setLoadingDetail(false);
          });
      }
    } else {
      setName('');
      setDescription('');
      setSymbols([]);
    }
    setSymbolInput('');
    return () => {
      cancelled = true;
    };
  }, [editing]);

  const addSymbols = (raw: string) => {
    const parsed = parseSymbols(raw);
    if (parsed.length === 0) return;
    setSymbols((prev) => Array.from(new Set([...prev, ...parsed])));
    setSymbolInput('');
  };

  const removeSymbol = (sym: string) => setSymbols((prev) => prev.filter((s) => s !== sym));

  const handlePasteImport = async () => {
    if (editing) {
      // Existing watchlist → hit the import endpoint (backend parses server-side).
      setSaving(true);
      const ok = await importWatchlist(editing.id, pasteText);
      if (ok) {
        try {
          const detail = await strategyLabApi.getWatchlist(editing.id);
          setSymbols(detail.symbols || []);
        } catch {
          setSymbols((prev) => Array.from(new Set([...prev, ...parseSymbols(pasteText)])));
        }
      }
      setSaving(false);
    } else {
      // New watchlist → merge parsed tickers into local draft.
      setSymbols((prev) => Array.from(new Set([...prev, ...parseSymbols(pasteText)])));
    }
    setPasteText('');
    setPasteOpen(false);
  };

  const canSave = name.trim().length > 0 && symbols.length > 0 && !saving;

  const handleSave = async () => {
    setSaving(true);
    const payload = { name: name.trim(), description: description.trim() || undefined, symbols };
    const result = editing
      ? await updateWatchlist(editing.id, payload)
      : await createWatchlist(payload);
    setSaving(false);
    if (result) onDone();
  };

  return (
    <Card className="bg-card">
      <CardHeader>
        <CardTitle className="text-lg">{editing ? 'Edit watchlist' : 'New watchlist'}</CardTitle>
        <CardDescription>
          {editing
            ? `Editing “${editing.name}”. Add or remove symbols, then save.`
            : 'Name the list, then add symbols individually or paste a bulk list.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Momentum leaders" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Description (optional)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="High RVOL large caps"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Add symbols</Label>
          <div className="flex gap-2">
            <Input
              value={symbolInput}
              onChange={(e) => setSymbolInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addSymbols(symbolInput);
                }
              }}
              placeholder="Type a ticker and press Enter (AAPL)"
            />
            <Button variant="outline" onClick={() => addSymbols(symbolInput)} disabled={!symbolInput.trim()}>
              <Plus className="h-4 w-4" /> Add
            </Button>
            <Button variant="outline" onClick={() => setPasteOpen(true)}>
              <ClipboardPaste className="h-4 w-4" /> Paste list
            </Button>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs text-muted-foreground">
              Symbols {symbols.length > 0 && <span className="font-semibold text-foreground">({symbols.length})</span>}
            </Label>
            {loadingDetail && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>
          {symbols.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No symbols yet.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto scrollbar-thin">
              {symbols.map((sym) => (
                <Badge key={sym} variant="secondary" className="gap-1 pr-1">
                  {sym}
                  <button
                    onClick={() => removeSymbol(sym)}
                    className="ml-0.5 rounded-full hover:bg-background/60 p-0.5"
                    title={`Remove ${sym}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {actionError && <p className="text-sm text-rose-500">{actionError}</p>}

        <div className="flex justify-end gap-2">
          {editing && (
            <Button variant="ghost" onClick={onDone} disabled={saving}>
              Cancel
            </Button>
          )}
          <Button onClick={handleSave} disabled={!canSave}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {editing ? 'Save changes' : 'Create watchlist'}
          </Button>
        </div>
      </CardContent>

      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Paste symbols</DialogTitle>
            <DialogDescription>
              Paste tickers separated by spaces, commas, or new lines. Invalid tokens are ignored.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={8}
            placeholder={'AAPL, MSFT, TSLA\nNVDA AMD\nGOOGL'}
          />
          <p className="text-xs text-muted-foreground">
            {parseSymbols(pasteText).length} valid symbol(s) detected.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasteOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handlePasteImport} disabled={parseSymbols(pasteText).length === 0 || saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardPaste className="h-4 w-4" />}
              {editing ? 'Import to watchlist' : 'Add to draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default WatchlistBuilder;
